import { ChevronLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';

import { cn } from '@/lib/cn';

interface PageHeaderProps {
  title: string;
  subtitle?: string;
  /**
   * Destination de repli quand il n'y a pas d'historique interne — arrivée
   * directe par un lien partagé, par exemple.
   */
  fallbackTo: string;
  className?: string;
}

/**
 * En-tête des écrans qui ne sont pas une destination de la barre basse :
 * un retour, puis le nom de l'écran. Repris de `.appbar` des maquettes.
 *
 * Ces écrans s'atteignent par le tiroir de compte, pas par un onglet — leur
 * montrer l'avatar et les compteurs comme le fait `AppTopBar` inviterait à
 * rouvrir le menu d'où l'on vient. Le retour est ce qui manque, pas le menu.
 */
export function PageHeader({
  title,
  subtitle,
  fallbackTo,
  className,
}: Readonly<PageHeaderProps>) {
  const { t } = useTranslation('nav');
  const navigate = useNavigate();
  const location = useLocation();

  // React Router marque `default` la toute première entrée : revenir en arrière
  // ferait alors quitter l'application. On retombe sur une destination connue.
  const handleBack = () => {
    if (location.key === 'default')
      void navigate(fallbackTo, { replace: true });
    else void navigate(-1);
  };

  return (
    <div className={cn('mb-4', className)}>
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={handleBack}
          aria-label={t('aria.back')}
          className="grid size-10 shrink-0 place-items-center rounded-panel border border-border bg-surface text-fg-muted transition-colors outline-none hover:bg-surface-hover hover:text-fg focus-visible:ring-2 focus-visible:ring-accent/60"
        >
          <ChevronLeft aria-hidden className="size-5" />
        </button>

        <h1 className="min-w-0 flex-1 truncate text-xl font-extrabold text-fg">
          {title}
        </h1>
      </div>

      {subtitle && <p className="mt-1 text-sm text-fg-subtle">{subtitle}</p>}
    </div>
  );
}
